/* eslint-disable jsdoc/require-jsdoc */
/* eslint-disable sort-exports/sort-exports */
/* eslint-disable jsdoc/require-param */
import type { T_Maybe, T_Optional } from '@tryiris-ai/js-types';

interface Deadband {
  /* Bottom vertical tolerance. */
  bvt: number;

  /* Horizontal margin constant. */
  hmc: number;

  /* Head room. */
  hr: number;

  /* Left horizontal tolerance. */
  lht: number;

  /* Right horizontal tolerance. */
  rht: number;

  /* Top vertical tolerance. */
  tvt: number;

  /* Vertical margin constant. */
  vmc: number;
}

export interface Bbox {
  height: number;
  width: number;
  x: number;
  y: number;
}

export interface Pt {
  x: number;
  y: number;
}

interface Deadband {
  bvt: number;
  hmc: number;
  hr: number;
  lht: number;
  rht: number;
  tvt: number;
  vmc: number;
}

/**
 * Calculates the constraints and tolerances.
 *
 * @param deadband - Deadband.
 * @returns Constraints and tolerances.
 */
export const calcToleranceBbox = (deadband: T_Maybe<T_Optional<Deadband>>): Bbox => {
  if (
    deadband?.hmc === undefined ||
    deadband.vmc === undefined ||
    deadband.hr === undefined ||
    deadband.lht === undefined ||
    deadband.rht === undefined ||
    deadband.tvt === undefined ||
    deadband.bvt === undefined
  ) {
    return {
      x: 0,
      y: 0,
      width: 0,
      height: 0
    };
  }

  // Calculate the tolerance deadband bounding box
  const x1 = deadband.hmc - deadband.lht;
  const y1 = deadband.vmc - deadband.tvt + deadband.hr;
  const x2 = deadband.hmc + deadband.rht;
  const y2 = deadband.vmc + deadband.bvt;

  return {
    x: x1,
    y: y1,
    width: x2 - x1,
    height: y2 - y1
  };
};

/**
 * Calculates the constraints and alignment.
 *
 * @param deadband - Deadband.
 * @returns Alignment bbox.
 */
export const calcAlignmentPt = (deadband: T_Maybe<T_Optional<Deadband>>): Pt => {
  if (
    deadband?.hmc === undefined ||
    deadband.vmc === undefined ||
    deadband.hr === undefined ||
    deadband.lht === undefined ||
    deadband.rht === undefined ||
    deadband.tvt === undefined ||
    deadband.bvt === undefined
  ) {
    return {
      x: 0,
      y: 0
    };
  }

  const x1 = deadband.hmc;
  const y1 = deadband.vmc + deadband.hr;

  return {
    x: x1,
    y: y1
  };
};

export const calcAlignmentDeadbandFromRelativeBbox = (
  toleranceDeadbandBbox: Bbox,
  alignmentDeadbandBboxRelative: Bbox
): Bbox => ({
  x: alignmentDeadbandBboxRelative.x + toleranceDeadbandBbox.x,
  y: alignmentDeadbandBboxRelative.y + toleranceDeadbandBbox.y,
  width: alignmentDeadbandBboxRelative.width,
  height: alignmentDeadbandBboxRelative.height
});

export const calcDeadbandFromBbox = (
  hr: number,
  toleranceDeadbandBbox: T_Maybe<T_Optional<Bbox>>,
  alignmentDeadbandPt: T_Maybe<T_Optional<Pt>>
): Deadband => {
  if (
    alignmentDeadbandPt?.x === undefined ||
    alignmentDeadbandPt.y === undefined ||
    toleranceDeadbandBbox?.x === undefined ||
    toleranceDeadbandBbox.y === undefined ||
    toleranceDeadbandBbox.width === undefined ||
    toleranceDeadbandBbox.height === undefined
  ) {
    return {
      bvt: 0,
      hmc: 0,
      hr,
      lht: 0,
      rht: 0,
      tvt: 0,
      vmc: 0
    };
  }

  const hmc = alignmentDeadbandPt.x;

  const vmc = alignmentDeadbandPt.y - hr;

  const lht = hmc - toleranceDeadbandBbox.x;

  const tvt = vmc - toleranceDeadbandBbox.y + hr;

  const rht = toleranceDeadbandBbox.x + toleranceDeadbandBbox.width - hmc;

  const bvt = toleranceDeadbandBbox.y + toleranceDeadbandBbox.height - vmc;

  return {
    bvt,
    hmc,
    hr,
    lht,
    rht,
    tvt,
    vmc
  };
};
